import type { LucideIcon } from "lucide-react"
import { CircleHelp, ShieldCheck, Sparkles, Wallet } from "lucide-react"

export type FaqCategoryId = "getting-started" | "technical" | "billing" | "privacy"

export type FaqItem = {
  id: string
  question: string
  answer: string
  category: FaqCategoryId
  readMoreHref?: string
  readMoreLabel?: string
}

export const FAQ_CATEGORY_META: Record<
  FaqCategoryId,
  { label: string; icon: LucideIcon }
> = {
  "getting-started": {
    label: "Getting started",
    icon: CircleHelp,
  },
  technical: {
    label: "Technical & accuracy",
    icon: Sparkles,
  },
  billing: {
    label: "Pricing & billing",
    icon: Wallet,
  },
  privacy: {
    label: "Privacy",
    icon: ShieldCheck,
  },
}

export const LANDING_FAQ_ITEMS: readonly FaqItem[] = [
  {
    id: "landing-what-is-askjyotish",
    category: "getting-started",
    question: "What is AskJyotish?",
    answer: `AskJyotish is an astrology platform that combines your exact birth chart with leading AI models—like ChatGPT, Gemini, and Claude—to give you highly personalized answers. Before we add any AI to our platform, our team tests it strictly to make sure its astrological readings are accurate and helpful. Once approved, the AI is released as a virtual "astrologer" for you to consult. Whenever a new AI model is created in the tech world, we will test it and add it as a new astrologer to our team.`,
  },
  {
    id: "landing-how-should-i-ask-questions",
    category: "getting-started",
    question: "How should I ask questions?",
    answer: `To get the most accurate and profound insights from our AI astrologers, the key is to provide clear context. Just like a human astrologer, the AI gives much better guidance when it understands the specific situation or problem you are facing.

❌ Vague Questions (Less effective):

"Predict my future."

"How will my married life be?"

"How will be my career?"

✅ Specific Questions (Highly effective):

"I have been trying to get married for the past two years, but proposals keep falling through at the last minute. When am I likely to find the right partner?"

"I am currently working in marketing but feel stuck and burnt out. Is this a good time for me to switch to a completely different industry like finance, or should I wait?"

"I recently lost my job and am feeling very anxious about my finances. When can I expect to find a stable position again, and what kind of roles should I aim for?"`,
  },
  {
    id: "landing-chatgpt-subscription",
    category: "technical",
    question: "Why can't I use ChatGPT directly?",
    answer: `While ChatGPT is a highly intelligent tool, it is a language model, not an astrological engine. It does not have native access to mathematically accurate birth charts. Even if you provide your birth details, general AI models frequently make mistakes when trying to calculate complex planetary positions. Because there is no transparency into how the AI comes up with these calculations, there is no guarantee of accuracy, leaving you at risk of being misled. AskJyotish solves this by generating your precise Vedic chart first and then feeding that verified data to the AI, ensuring completely trustworthy readings.`,
  },
  {
    id: "landing-our-philosophy",
    category: "getting-started",
    question: "Why did we build this?",
    answer: `We built AskJyotish to make authentic Vedic astrology accessible to everyone. Our platform is rooted in complete transparency and the highest levels of accuracy. It serves as a safe space to seek trustworthy guidance when a trusted astrologer is unavailable, ensuring you do not have to worry about being misled or scammed by amateurs.
Our goal is not to replace human astrologers or take away their livelihood. Instead, our mission is to respect traditional wisdom and use the power of modern AI to make ancient astrological teachings easier for everyone to access and understand.`,
  },
  {
    id: "landing-our-techniques",
    category: "technical",
    question: "How do we read your charts?",
    answer: `The foundation of our astrology is built on the timeless wisdom and ancient principles found in the Vedas and Upanishads. However, to give you the most accurate and practical insights, we also incorporate modern astrological techniques taught by highly respected institutions like Bharatiya Vidya Bhavan (BVB). By blending deep ancient traditions with refined modern research, we ensure that your readings are authentic, highly accurate, and relevant to your life today.`,
  },
  {
    id: "landing-birth-time-range",
    category: "technical",
    question: "Why is my birth time treated as a range?",
    answer: `Even if you know your exact hospital birth time, the true definition of a "birth time" is highly nuanced. Is it the moment the baby’s head first appears, when the body is fully out, or when the umbilical cord is cut? In Vedic astrology, even a one-minute difference can completely change your chart and readings.
To account for this, we treat your birth time as a small window rather than a single exact minute. Our AI astrologers analyze this entire time frame to identify which planetary positions and charts remain stable and unchanged. By only making predictions based on these stable factors, we ensure your readings are incredibly accurate and not thrown off by a slight variation on the hospital clock.`,
  },
  {
    id: "landing-btr",
    category: "technical",
    question: "What is Birth Time Rectification?",
    answer: `BTR is a traditional astrological technique used when a person does not know their exact birth time. Rather than guessing, this method works backwards by looking at major events that have already happened in your life.
Soon, our platform will introduce an AI-assisted BTR feature. You will be able to share significant past milestones, and our AI will carefully match these events against planetary timelines. By aligning your actual life history with astrological movements, the system can calculate your precise time of birth, ensuring your chart and future readings are entirely accurate.`,
  },
  {
    id: "landing-family-friends-charts",
    category: "getting-started",
    question: "How do I add charts for my family members or friends?",
    answer: `AskJyotish lets you save multiple people in one account, so you can create separate chart profiles for yourself, your partner, children, parents, siblings, friends, or anyone else you want to consult about.

To add someone, go to the People section and tap Add person. Enter their basic birth details such as name, relationship, birth date, birth time, and birth place. Once saved, AskJyotish creates that person’s chart profile separately.`,
  },
  {
    id: "landing-personal-data-protection",
    category: "privacy",
    question: "How is my personal data and birth information protected?",
    answer: `We treat your birth details, saved profiles, and chat history as sensitive information. AskJyotish uses security safeguards designed to protect your data from unauthorized access, misuse, or disclosure. Our system is designed to use encryption in transit and standard encrypted storage controls for sensitive data.

Your information is not sold to third parties, and it is not visible to other users on the platform. However, some data may be processed by trusted service providers that help us operate AskJyotish, such as hosting, payments, authentication, and AI model providers acting on our behalf.

If you share personal details in chat, we use that information to personalize readings, and support the features you request.`,
  },
  {
    id: "landing-account-deletion",
    category: "privacy",
    question: "Can I delete my account and data?",
    answer: `Yes. You can request account deletion from the Settings section of the app.

When you request deletion, we remove personal account data as required by our system and legal obligations. In some cases, limited records may be retained for billing, audit, fraud prevention, dispute resolution, or compliance purposes for a limited period.

If you have a privacy question or need help with a data request, you can also contact support.`,
  },
] as const

export const FAQ_PAGE_ITEMS: readonly FaqItem[] = [
  {
    id: "faq-what-is-askjyotish",
    category: "getting-started",
    question: "What is AskJyotish?",
    answer: `AskJyotish is an astrology platform that combines your exact birth chart with leading AI models—like ChatGPT, Gemini, and Claude—to give you highly personalized answers. Before we add any AI to our platform, our team tests it strictly to make sure its astrological readings are accurate and helpful. Once approved, the AI is released as a virtual "astrologer" for you to consult. Whenever a new AI model is created in the tech world, we will test it and add it as a new astrologer to our team.`,
  },
  {
    id: "faq-how-should-i-ask-questions",
    category: "getting-started",
    question: "How should I ask questions?",
    answer: `To get the most accurate and profound insights from our AI astrologers, the key is to provide clear context. Just like a human astrologer, the AI gives much better guidance when it understands the specific situation or problem you are facing.

❌ Vague Questions (Less effective):

"Predict my future."

"How will my married life be?"

"How will be my career?"

✅ Specific Questions (Highly effective):

"I have been trying to get married for the past two years, but proposals keep falling through at the last minute. When am I likely to find the right partner?"

"I am currently working in marketing but feel stuck and burnt out. Is this a good time for me to switch to a completely different industry like finance, or should I wait?"

"I recently lost my job and am feeling very anxious about my finances. When can I expect to find a stable position again, and what kind of roles should I aim for?"`,
  },
  {
    id: "faq-chatgpt-subscription",
    category: "technical",
    question: "Why can't I use ChatGPT directly?",
    answer: `While ChatGPT is a highly intelligent tool, it is a language model, not an astrological engine. It does not have native access to mathematically accurate birth charts. Even if you provide your birth details, general AI models frequently make mistakes when trying to calculate complex planetary positions. Because there is no transparency into how the AI comes up with these calculations, there is no guarantee of accuracy, leaving you at risk of being misled. AskJyotish solves this by generating your precise Vedic chart first and then feeding that verified data to the AI, ensuring completely trustworthy readings.`,
  },
  {
    id: "faq-our-philosophy",
    category: "getting-started",
    question: "Why did we build this?",
    answer: `We built AskJyotish to make authentic Vedic astrology accessible to everyone. Our platform is rooted in complete transparency and the highest levels of accuracy. It serves as a safe space to seek trustworthy guidance when a trusted astrologer is unavailable, ensuring you do not have to worry about being misled or scammed by amateurs.
Our goal is not to replace human astrologers or take away their livelihood. Instead, our mission is to respect traditional wisdom and use the power of modern AI to make ancient astrological teachings easier for everyone to access and understand.`,
  },
  {
    id: "faq-our-techniques",
    category: "technical",
    question: "How do we read your charts?",
    answer: `The foundation of our astrology is built on the timeless wisdom and ancient principles found in the Vedas and Upanishads. However, to give you the most accurate and practical insights, we also incorporate modern astrological techniques taught by highly respected institutions like Bharatiya Vidya Bhavan (BVB). By blending deep ancient traditions with refined modern research, we ensure that your readings are authentic, highly accurate, and relevant to your life today.`,
  },
  {
    id: "faq-birth-time-range",
    category: "technical",
    question: "Why is my birth time treated as a range?",
    answer: `Even if you know your exact hospital birth time, the true definition of a "birth time" is highly nuanced. Is it the moment the baby’s head first appears, when the body is fully out, or when the umbilical cord is cut? In Vedic astrology, even a one-minute difference can completely change your chart and readings.
To account for this, we treat your birth time as a small window rather than a single exact minute. Our AI astrologers analyze this entire time frame to identify which planetary positions and charts remain stable and unchanged. By only making predictions based on these stable factors, we ensure your readings are incredibly accurate and not thrown off by a slight variation on the hospital clock.`,
  },
  {
    id: "faq-btr",
    category: "technical",
    question: "What is Birth Time Rectification?",
    answer: `BTR is a traditional astrological technique used when a person does not know their exact birth time. Rather than guessing, this method works backwards by looking at major events that have already happened in your life.
Soon, our platform will introduce an AI-assisted BTR feature. You will be able to share significant past milestones, and our AI will carefully match these events against planetary timelines. By aligning your actual life history with astrological movements, the system can calculate your precise time of birth, ensuring your chart and future readings are entirely accurate.`,
  },
  {
    id: "faq-detailed-analysis",
    category: "technical",
    question: "What is detailed analysis mode?",
    answer: `Detailed analysis mode adds a deeper astrological breakdown below the main answer.

When it is enabled, AskJyotish shows not only the direct answer, but also the supporting analysis behind it. This may include the relevant chart factors, planetary placements, house themes, dasha context, transit influences, and the reasoning used to arrive at the reading.`,
  },
  {
    id: "faq-family-friends-charts",
    category: "getting-started",
    question: "How do I add charts for my family members or friends?",
    answer: `AskJyotish lets you save multiple people in one account, so you can create separate chart profiles for yourself, your partner, children, parents, siblings, friends, or anyone else you want to consult about.

To add someone, go to the People section and tap Add person. Enter their basic birth details such as name, relationship, birth date, birth time, and birth place. Once saved, AskJyotish creates that person’s chart profile separately.`,
  },
  {
    id: "faq-personal-data-protection",
    category: "privacy",
    question: "How is my personal data and birth information protected?",
    answer: `We treat your birth details, saved profiles, and chat history as sensitive information. AskJyotish uses security safeguards designed to protect your data from unauthorized access, misuse, or disclosure. Our system is designed to use encryption in transit and standard encrypted storage controls for sensitive data.

Your information is not sold to third parties, and it is not visible to other users on the platform. However, some data may be processed by trusted service providers that help us operate AskJyotish, such as hosting, payments, authentication, and AI model providers acting on our behalf.

If you share personal details in chat, we use that information to personalize readings, and support the features you request.`,
  },
  {
    id: "faq-account-deletion",
    category: "privacy",
    question: "Can I delete my account and data?",
    answer: `Yes. You can request account deletion from the Settings section of the app.

When you request deletion, we remove personal account data as required by our system and legal obligations. In some cases, limited records may be retained for billing, audit, fraud prevention, dispute resolution, or compliance purposes for a limited period.

If you have a privacy question or need help with a data request, you can also contact support.`,
  },
  {
    id: "faq-add-person",
    category: "getting-started",
    question: "How do I add a person to my account?",
    answer:
      'Go to the People section from the sidebar and tap "New person". Enter their name, date of birth, time of birth, and place of birth to generate their chart.',
  },
  {
    id: "faq-default-astrologer",
    category: "getting-started",
    question: "Can I change my default astrologer?",
    answer:
      "Yes. Open Settings and scroll to the Astrologer section, then pick any available astrologer to use as the default for new conversations.",
  },
  {
    id: "faq-default-person",
    category: "getting-started",
    question: "Can I change my default person?",
    answer:
      "Yes. Open Settings and choose a different default person from the People section if you want new chats to start with someone else.",
  },
  {
    id: "faq-delete-account",
    category: "privacy",
    question: "Can I delete my account?",
    answer:
      "Yes. Open Settings, go to the account section, and use Delete account to request deletion of your account data.",
  },
] as const
